import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import AvatarChip from '../../../../shared/avatar-chip';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import BulkOpTag from './bulk-op-tag';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Groups from '@mui/icons-material/Groups';

interface StepGroupConfigProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  control: Control<BulkOpsFormValues>;
  isNewUi?: boolean;
}

export default function StepGroupConfig({
  setValue,
  watch,
  control,
  isNewUi = false,
}: StepGroupConfigProps) {
  const theme = useTheme();
  const sources = watch('sources');
  const includeNestedGroups = watch('includeNestedGroups') || [];

  const toggleNestedGroups = (group: string) => {
    if (includeNestedGroups.includes(group)) {
      const filteredGroups = includeNestedGroups.filter(
        (includedGroup) => includedGroup !== group
      );
      setValue('includeNestedGroups', filteredGroups);
    } else {
      setValue('includeNestedGroups', [...includeNestedGroups, group]);
    }
  };

  if (isNewUi) {
    const rowBorder = `0.5px solid ${theme.palette.semantic.outline.light}`;
    const labelTypography = {
      typography: {
        variant: 'body2' as const,
        color: theme.palette.semantic.text.body,
      },
    };

    return (
      <Stack gap={3} width={'100%'} sx={{ flex: 1, minHeight: 0 }}>
        <FormSectionHeader
          descriptionVariant="body2"
          description="Optionally choose any of your selected groups, if you would like to have included members of the nested groups in them."
        >
          Group Configuration
        </FormSectionHeader>
        <Stack
          sx={{
            border: rowBorder,
            borderRadius: 2,
            overflow: 'hidden',
            minHeight: 0,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
            {(sources ?? []).map((group) => (
              <Stack
                key={group}
                direction="row"
                alignItems="center"
                sx={{ minHeight: 60, borderBottom: rowBorder }}
              >
                <Box sx={{ width: 450, flexShrink: 0, minWidth: 0, p: 2 }}>
                  <BulkOpTag label={group} icon={Groups} />
                </Box>
                {/* Design: 56px checkbox cell, then a 16px-padded label cell. */}
                <FormControlLabel
                  sx={{ m: 0, pl: 3, gap: 3, flex: 1 }}
                  control={
                    <Checkbox
                      checked={includeNestedGroups.includes(group)}
                      onChange={() => toggleNestedGroups(group)}
                      sx={{ p: 0 }}
                    />
                  }
                  label="Include Members of Nested Groups"
                  slotProps={labelTypography}
                />
              </Stack>
            ))}
          </Box>
          <Box sx={{ minHeight: 60, display: 'flex', alignItems: 'center' }}>
            <Controller
              name="createGroups"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  sx={{ m: 0, pl: 2, gap: 2 }}
                  control={
                    <Checkbox
                      {...props}
                      checked={!!props.value}
                      sx={{ p: 0 }}
                    />
                  }
                  label="Auto-create groups that do not exist"
                  slotProps={{
                    typography: {
                      variant: 'caption',
                      color: theme.palette.semantic.text.body,
                    },
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={1} width={'100%'} height={'100%'} sx={{ flex: 1 }}>
      <Typography sx={{ mb: 1 }}>
        Optionally choose any of your selected groups, if you would like to have
        included members of the nested groups in them.
      </Typography>
      <Stack
        gap={1}
        sx={{
          textOverflow: 'ellipsis',
          width: '100%',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {sources && sources.length > 0
          ? sources.map((group) => {
              return (
                <Grid container key={group} sx={{ alignItems: 'center' }}>
                  <Grid size={{ xs: 5 }}>
                    <AvatarChip label={group} type={'Group'} email={group} />
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeNestedGroups.includes(group)}
                          onChange={() => toggleNestedGroups(group)}
                        />
                      }
                      label="Include Members of Nested Groups"
                    />
                  </Grid>
                </Grid>
              );
            })
          : null}
      </Stack>
      <Stack>
        <Controller
          name="createGroups"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={<Checkbox {...props} />}
              label="Auto-create groups that do not exist"
            />
          )}
        />
      </Stack>
    </Stack>
  );
}
